import { useEffect, useState } from 'react';

import Icon from '@klv/atoms/icon';
import Button from '@klv/atoms/button';
import Buttons from '@klv/molecules/buttons';
import { generateClassName } from '@klv/shared/utils/functions';

import { InformationIcon } from '../informationIcon';
import * as s from './styles/flowForm.module.scss';

import { flows } from '../../data';
import useActiveElement from './hooks/useActiveElement';
import type { TFlow } from '../../types';
import type { IFlowForm } from './types';


export const FlowForm = ({ setFlow, setPage }: IFlowForm) => {
  const [selectedFlow, setSelectedFlow] = useState<TFlow | null>(null);
  const { showElement, setRef, slide } = useActiveElement();

  // reset the flow when going back to the 2nd step (pre generated flow)
  useEffect(() => {
    if (slide < 2) { setFlow(null); }
  }, [slide, setFlow]);

  // handle the flow change to take animation timing into effect.
  const handleChange = (step: number) => {
    if (step === 2) { // wait for the animation to finish
      setTimeout(() => setFlow(selectedFlow), 500);
    }
    if (step === 0) { // this needs to be just a little under the animation time to avoid a flash of the previous selected flow
      setTimeout(() => setSelectedFlow(null), 240);
    }

    showElement(step);
  };

  return (
    <>
      {/* Step 0 is our Flow selection slide */}
      <div ref={node => setRef(node, 0)} data-is-active={slide === 0} className={generateClassName(['flowsAiInitialStep', s.initialStep])}>
        <div className={s.flowForm}>
          <div>
            <div className={s.title}>Choose a flow</div>
            <p className="p2">See how the magic of AI takes a description and brings it to life in a built-out flow.</p>

            {/* Loop through the flows to create buttons / mock checkbox */}
            {Object.keys(flows).map((flowIndex) => (
              <button 
                key={flowIndex}
                className={generateClassName([
                  s.flowButton,
                  selectedFlow === flowIndex ? s.selected : '',
                ])}
                onClick={() => {setSelectedFlow(flowIndex as TFlow);}}
              >
                <span>{flows[flowIndex].name}</span>
                
                {selectedFlow !== flowIndex ? (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="1" y="1" width="14" height="14" rx="3" fill="white" stroke="#D0D5DD" stroke-width="2"/>
                    <path d="M3.90039 8L6.30039 10.4L11.5005 5.5" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="1" y="1" width="14" height="14" rx="3" fill="#EF6351" stroke="#EF6351" stroke-width="2"/>
                    <path d="M3.90039 8L6.30039 10.4L11.5005 5.5" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
                  </svg>
                )}
              </button>
            ))}
            <Buttons
              className={s.buttons}
              buttonColor='dark'
              variants={['ghost', 'primary']}
              buttons={[
                { text: 'Back', action: () => {setPage('hero'); setSelectedFlow(null);} },
                { text: 'Continue', action: () => handleChange(1), disabled: !selectedFlow },
              ]}
            />
          </div>
        </div>
      </div>
      
      {/* Loop through steps 1 - 3 */}
      {[1, 2, 3].map((slideNumber) => (
        <div key={slideNumber} ref={node => setRef(node, slideNumber)} className={generateClassName([s.step])}>
          <div className={generateClassName([s.flowForm])}>
            {/* We're going to show the toggle label here on slide 2 */}
            {[2, 3].includes(slideNumber) && (
              <>
                <input defaultChecked type="checkbox" id={`mobileDescriptionToggle-${slideNumber}`} className={s.mobileDescriptionCheckbox}/>
                <label htmlFor={`mobileDescriptionToggle-${slideNumber}`} className={s.mobileDescriptionCheckboxLabel}>
                  <Icon className={s.mobileDescriptionCheckboxIcon} iconName="ChevronNorth" color='currentColor'/>
                  <span>View description</span>
                  <span>Hide description</span>
                </label>
              </>
            )}

            {/* Slide indicators: Note these indicators are not displayed on the first or final frame */}
            <div className={s.slideIndicators}>
              {[1, 2, 3, 4].map((indicator) => (
                <div className={generateClassName([s.slideIndicator, slideNumber === indicator ? s.selected : ''])} key={indicator}/>
              ))}
            </div>
            <div className={s.flowDescriptionWrapper}>
              {/* display the appropriate slide title */}
              <div className={generateClassName([s.title, s.slideTitle])}>
                {[
                  'Great! Now generate a flow from our sample description.',
                  'It’s that easy. Your flow structure will match the logic and actions you describe.',
                  'Once your flow structure is built, you can add the message content.',
                ][slideNumber - 1]}
              </div>

              <div className={s.divider}></div>
              
              {/* on slides 1 & 2, shows the flow mock textarea and labels */}
              {slideNumber < 3 && (
                <>
                  <div className={s.flowName}>{ selectedFlow && flows[selectedFlow].name }</div>
                  <label className={s.textareaLabel}>
                    Describe the flow
                    <small>Include details about flow start, actions, delays, and logic.</small>
                  </label>
                  <div className={s.textarea}>
                    { selectedFlow && flows[selectedFlow].prompt }
                  </div>
                </>
              )}

              {/* on slide 3, show the flow conclusion & information icon.
                * The information icon is used in CSS to apply correct text alignment to the parent container 
              */}
              {slideNumber === 3 && (
                <>
                  <InformationIcon />
                  <p className={generateClassName(['p2', s.conclusion])}>
                    {selectedFlow && flows[selectedFlow].conclusion}
                  </p>
                </>
              )}
            </div>
            
            <Buttons
              className={s.buttons}
              buttonColor='dark'
              variants={['ghost', 'primary']}
              buttons={[
                { text: 'Back', action: () => handleChange(slideNumber - 1) },
                { text: slideNumber === 1 ? 'Generate' : 'Next', action: () =>  handleChange(slideNumber + 1) },
              ]}
            />
            
          </div>
        </div>
      ))}

      {/* Step 4 */}
      <div ref={node => setRef(node, 4)} className={generateClassName([s.step, s.lastStep])}>
        <div className={s.flowForm}>
          <div className="h5 semibold">Ready to grow faster with Flows AI?</div>
          <p className="p2">Klaviyo customers with a paid account and over 250 active profiles get access to Flows AI.</p>
          <div className={s.ctas}>
            <Button variant='primary' text="Sign up" url="/sign-up"/>
            <div className={s.or}><span>OR</span></div>
            <Button variant='ghost' text="Watch platform demo" url="/product/platform-tour"/>
            <Button variant='ghost' text="See how Lifestraw saves time with Flows AI" url="/customers/case-studies/lifestraw"/>
            <Button variant='ghost' text="Create another flow" action={() =>{handleChange(0);}}/>
          </div>
        </div>
      </div>
    </>
  );
};
